// Users Service - Manage user data in public.users table

import { Injectable } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { getSupabaseClient } from '../supabase/supabase.client';
import { User, UserRole } from '../../models';
import type { Language } from '../../models/user.model';

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  private supabase = getSupabaseClient();

  /**
   * Get user by ID
   */
  getUserById(userId: string): Observable<User | null> {
    return from(
      this.supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single()
    ).pipe(
      map(({ data, error }) => {
        if (error || !data) {
          console.error('Error fetching user:', error);
          return null;
        }
        return this.mapDatabaseUser(data);
      }),
      catchError((error) => {
        console.error('Error in getUserById:', error);
        return of(null);
      })
    );
  }

  /**
   * Get users by role
   */
  getUsersByRole(role: UserRole): Observable<User[]> {
    return from(
      this.supabase
        .from('users')
        .select('*')
        .eq('role', role)
        .eq('is_active', true)
    ).pipe(
      map(({ data, error }) => {
        if (error || !data) {
          console.error('Error fetching users by role:', error);
          return [];
        }
        return data.map(this.mapDatabaseUser);
      }),
      catchError((error) => {
        console.error('Error in getUsersByRole:', error);
        return of([]);
      })
    );
  }

  /**
   * Update user profile
   */
  updateUser(userId: string, updates: Partial<User>): Observable<User | null> {
    // Map frontend model to database columns
    const dbUpdates: Record<string, any> = {};
    
    if (updates.firstName !== undefined) dbUpdates['first_name'] = updates.firstName;
    if (updates.lastName !== undefined) dbUpdates['last_name'] = updates.lastName;
    if (updates.phone !== undefined) dbUpdates['phone'] = updates.phone;
    if (updates.avatarUrl !== undefined) dbUpdates['avatar_url'] = updates.avatarUrl;
    if (updates.preferredLanguage !== undefined) dbUpdates['preferred_language'] = updates.preferredLanguage;

    return from(
      this.supabase
        .from('users')
        // @ts-ignore - Database types are out of sync with actual schema
        .update(dbUpdates)
        .eq('id', userId)
        .select()
        .single()
    ).pipe(
      map(({ data, error }) => {
        if (error || !data) {
          console.error('Error updating user:', error);
          return null;
        }
        return this.mapDatabaseUser(data);
      }),
      catchError((error) => {
        console.error('Error in updateUser:', error);
        return of(null);
      })
    );
  }

  /**
   * Update client profile preferred language (legacy + backend compatibility).
   */
  updateClientPreferredLanguage(userId: string, language: Language): Observable<boolean> {
    return from(
      this.supabase
        .from('client_profiles')
        // @ts-ignore - Generated DB types may lag schema details
        .update({ preferred_language: language } as any)
        .eq('user_id', userId)
    ).pipe(
      map(({ error }) => {
        if (error) {
          console.error('Error updating client preferred language:', error);
          return false;
        }
        return true;
      }),
      catchError((error) => {
        console.error('Error in updateClientPreferredLanguage:', error);
        return of(false);
      })
    );
  }

  /**
   * Map database row to User model
   */
  private mapDatabaseUser(data: any): User {
    return {
      id: data.id,
      email: data.email,
      role: data.role as UserRole,
      preferredLanguage: data.preferred_language || undefined,
      firstName: data.first_name,
      lastName: data.last_name,
      phone: data.phone || undefined,
      avatarUrl: data.avatar_url || undefined,
      isActive: data.is_active,
      chatFilterWarningAt: data.chat_filter_warning_at
        ? new Date(data.chat_filter_warning_at)
        : undefined,
      emailVerified: data.email_verified,
      lastLoginAt: data.last_login_at ? new Date(data.last_login_at) : undefined,
      stripeCustomerId: data.stripe_customer_id || undefined,
      stripeAccountId: data.stripe_account_id || undefined,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at),
    };
  }
}
